import { useState, useEffect } from 'react';
import { ComicCard } from './components/ComicCard';
import { HeroSection } from './components/HeroSection';
import { CategoryNav } from './components/CategoryNav';
import { RankingSidebar } from './components/RankingSidebar';
import { hotComics, newComics, rankingComics, latestUpdates, comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Home() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredComics, setFilteredComics] = useState<Comic[]>(comics);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredComics(comics);
    } else {
      setFilteredComics(comics.filter(c => c.categorySlug === selectedCategory));
    }
  }, [selectedCategory]);

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '韩漫网',
        url: 'https://hanmanwang.com',
        description: '韩漫画免费阅读平台，提供韩漫全集大全，下拉式无广告阅读',
        sameAs: ['https://weibo.com/hanmanwang', 'https://weixin.qq.com/hanmanwang'],
      },
      {
        '@type': 'WebSite',
        name: '韩漫网',
        url: 'https://hanmanwang.com',
        description: '来到韩漫网，您将免费阅读全集的韩漫大全，采用下拉式无广告阅读模式，随时随地畅享各种漫画内容，提升您的在线娱乐体验。',
        inLanguage: 'zh-Hans',
      },
      {
        '@type': 'WebPage',
        name: '韩漫画免费-韩漫全集大全-漫画下拉式无广告-韩漫网',
        url: 'https://hanmanwang.com/',
        isPartOf: { '@type': 'WebSite', name: '韩漫网', url: 'https://hanmanwang.com' },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: 'https://hanmanwang.com/og-image.jpg',
        },
        inLanguage: 'zh-Hans',
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchema) }}
      />

      <HeroSection />

      <main>
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950">
                热门推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                大家都在看的韩漫精选
              </p>
            </div>
            <a
              href="/category/all"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              查看全部
              <i className="ri-arrow-right-s-line"></i>
            </a>
          </div>
          <div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5"
            data-product-shop
          >
            {hotComics.slice(0, 6).map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        <CategoryNav
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading text-foreground-950">
                    最新更新
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">
                    每日更新，精彩不断
                  </p>
                </div>
              </div>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5"
                data-product-shop
              >
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>

            <aside className="w-full lg:w-[300px] flex-shrink-0">
              <RankingSidebar comics={rankingComics} />
            </aside>
          </div>
        </section>

        <section className="bg-background-100/70 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950">
                新作上线
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                最新上架的韩漫新作，等你来发现
              </p>
            </div>
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5"
              data-product-shop
            >
              {newComics.concat(hotComics.filter(c => !c.isNew)).slice(0, 6).map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="relative rounded-2xl overflow-hidden bg-primary-500/10 p-8 md:p-12 text-center">
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-4">
                随时随地，畅享韩漫
              </h2>
              <p className="text-foreground-600 mb-6 leading-relaxed">
                韩漫网提供<strong>韩漫画免费</strong>阅读服务，收录<strong>韩漫全集大全</strong>，
                采用便捷的<strong>漫画下拉式无广告</strong>阅读模式，让您在任何设备上都能畅享优质漫画内容。
              </p>
              <a
                href="/category/all"
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                马上开始阅读
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}